import { useScreen } from '@/hooks/useScreen';
import { Button, Slide } from '@mui/material';
import { useContext, useEffect, useRef, useState } from 'react';
import { AiOutlinePlus } from 'react-icons/ai';
import SwiperClass, { Mousewheel, Navigation, Pagination } from 'swiper';
import 'swiper/css';
import 'swiper/css/pagination';
import { Swiper, SwiperSlide } from 'swiper/react';
import useFetchBusinessProfileGroup from '../../hooks/profile/business/useFetchBusinessProfileGroup';
import useGetTMSUserIdByEmail from '../../hooks/useGetTMSUserIdByEmail';
import {
  IUserProfileData,
  RoleViewEnum,
} from '../../interfaces/profile.interface';
import BusinessGroupPreview, {
  BusinessGroupPreviewSkeleton,
} from '../elements/profile/business/BusinessGroupPreview';
import { BusinessProfileModeContext } from '../elements/profile/business/BusinessProfileModeProvider';

const BusinessGroupProfileGallery = () => {
  const { initData: profileData } = useContext(BusinessProfileModeContext);
  const containerRef = useRef(null);
  const email = (profileData as IUserProfileData)?.profiles?.qbankData?.email;
  const isOwnerProfile = profileData?.viewPermission === RoleViewEnum.Owner;
  const pageSize = 4;
  const {
    queryReturn: { data: tmsUserId },
  } = useGetTMSUserIdByEmail(email);
  const {
    data: { data, meta },
    queryReturn: { isLoading, isFetchingNextPage, fetchNextPage },
  } = useFetchBusinessProfileGroup({ pageSize, tmsUserId });

  const renderCard = () =>
    data.map((item, index) => (
      <Slide
        in
        direction="right"
        key={item.id}
        container={containerRef.current}
        unmountOnExit
        timeout={{
          enter: 500,
          appear: 500 * index,
        }}
      >
        <div className="h-full">
          <BusinessGroupPreview {...item} isOwnerProfile={isOwnerProfile} />
        </div>
      </Slide>
    ));

  return (
    <div ref={containerRef} className="flex flex-col gap-2">
      <h1 className="mb-1 mt-4 text-lg font-semibold leading-[130%] tracking-[-0.4px]">
        Cộng đồng nổi bật
      </h1>
      {!isLoading ? (
        <SwiperWrapper
          fetchNextPage={fetchNextPage}
          isFetchingNextPage={isFetchingNextPage}
          showMoreButton={meta.hasNextPage}
          displayList={renderCard() || []}
        />
      ) : (
        <SwiperWrapper
          displayList={new Array(4).fill(0).map((_, index) => (
            <BusinessGroupPreviewSkeleton key={index} />
          ))}
          showMoreButton={false}
        />
      )}
    </div>
  );
};

function SwiperWrapper({
  displayList,
  isFetchingNextPage = false,
  fetchNextPage = () => {},
  showMoreButton = true,
}: {
  displayList: React.ReactElement[];
  isFetchingNextPage?: boolean;
  fetchNextPage?: Function;
  showMoreButton?: boolean;
}) {
  const { isTablet, isMobile, isDesktop } = useScreen();

  // store controlled swiper instance
  const [controlledSwiper, setControlledSwiper] = useState<SwiperClass | null>(
    null
  );
  useEffect(() => {
    isTablet ? controlledSwiper?.disable() : controlledSwiper?.enable();
  }, [controlledSwiper, isTablet]);
  return (
    <Swiper
      direction={!isMobile ? 'vertical' : 'horizontal'}
      modules={[Mousewheel, Pagination, Navigation]}
      mousewheel={true}
      spaceBetween={16} //because GroupSuggestion css to make shadow is not clipped
      slidesPerView={isMobile ? 2 : 'auto'}
      watchSlidesProgress={true}
      className="!w-[calc(100vw-3rem)] overflow-visible lg:!w-[calc(100vw-250px)] lg:!max-w-[220px]"
      onSwiper={(swiper) => setControlledSwiper(swiper)}
    >
      {displayList.map((item, index) => {
        return (
          <SwiperSlide
            className={`not-custom ${isDesktop ? 'pb-0' : 'pb-4'} !h-auto`}
            key={`slider-profile-${index}`}
          >
            {item}
          </SwiperSlide>
        );
      })}
      {isFetchingNextPage &&
        new Array(4).fill(0).map((_value, index) => (
          <SwiperSlide
            className="not-custom !h-auto pb-4"
            key={`slider-profile-${index}`}
          >
            <BusinessGroupPreviewSkeleton />
          </SwiperSlide>
        ))}
      {showMoreButton && !isFetchingNextPage ? (
        <SwiperSlide className="not-custom flex !h-auto items-center justify-center pb-4 ">
          <Button
            onClick={() => fetchNextPage()}
            className={`!border !normal-case ${
              isDesktop
                ? '!border-transparent'
                : '!border-[#009FE4] !bg-[#B1E7FF33]'
            } !rounded-[20px] !border-solid  !px-[10px] !py-[6px] !text-xs !font-semibold !leading-4 !text-[#009CE0]`}
          >
            <div className="flex items-center justify-between gap-[12.5px]">
              <span className="flex items-center text-[#009CE0]">
                <AiOutlinePlus />
              </span>
              <span className="text-xs font-semibold text-[#009CE0]">
                Xem thêm
              </span>
            </div>
          </Button>
        </SwiperSlide>
      ) : null}
    </Swiper>
  );
}
export default BusinessGroupProfileGallery;
